import React, { useEffect, useRef, useState } from "react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { ArrowLeft, ArrowRight as ChevR } from "@phosphor-icons/react";

/**
 * FotoMega — la SEZIONE FOTO della nuova landing. Mostra TUTTE le 9
 * trasformazioni esistenti, una alla volta in un BeforeAfterSlider
 * GIGANTE (full-width su mobile, max 720px desktop) + galleria
 * orizzontale scrollabile sotto con i thumbnail di tutte le stanze.
 *
 * iter v31 r20 — User: "Foto deve avere massima visibilità,
 * voglio vedere tutte le foto, deve essere pazzesco."
 *
 * Pattern: Apple-style. Header sticky stanza attiva. Slider grande.
 * Strip thumbnail orizzontale scrollabile (no autoplay che cambia
 * stanza da solo — l'utente decide).
 */

const ROOMS = [
  {
    id: "camera",
    label: "Camera",
    badge: "Foto migliorata",
    before: "/landing/camera-before.jpg",
    after: "/landing/camera-after.jpg",
    tagline: "Letto rifatto, oggetti via. La stanza si presenta da sola.",
    tech: "Riordina la stanza · luce equilibrata · linee dritte · colori naturali",
  },
  {
    id: "rinnova",
    label: "Restyling",
    badge: "Arredo aggiornato",
    before: "/landing/rinnova-before.jpg",
    after: "/landing/rinnova-after.jpg",
    tagline: "Cambia l'arredo, la stanza resta la stessa.",
    tech: "Nuovi mobili e finiture · muri, finestre e pavimento invariati",
  },
  {
    id: "vuoto",
    label: "Arreda",
    badge: "Staging digitale",
    before: "/landing/vuoto-before.jpg",
    after: "/landing/vuoto-after.jpg",
    tagline: "Stanza vuota? La mostri già arredata.",
    tech: "Aggiunge mobili in proporzione · stile coerente con la stanza",
  },
  {
    id: "sala",
    label: "Living",
    badge: "Foto migliorata",
    before: "/landing/sala-original-before.jpg",
    after: "/landing/sala-original-after.jpg",
    tagline: "Luce, colori e prospettiva sistemati in un tocco.",
    tech: "Luce equilibrata · prospettiva corretta · colori fedeli",
  },
  {
    id: "pianta",
    label: "Planimetria",
    badge: "Da catasto → leggibile",
    before: "/landing/pianta-before.jpg?v=27",
    after: "/landing/pianta-after.jpg?v=27",
    tagline: "La planimetria del catasto, finalmente leggibile.",
    tech: "Da catasto a 2D arredata · stanze, m² e orientamento etichettati",
  },
  {
    id: "cucina",
    label: "Cucina",
    badge: "Luce ottimizzata",
    before: "/landing/cucina-before.jpg",
    after: "/landing/cucina-after.jpg",
    tagline: "La cucina di sempre, senza flash e ombre dure.",
    tech: "Luce equilibrata · superfici pulite · dettagli nitidi",
  },
  {
    id: "bagno",
    label: "Bagno",
    badge: "Pronta per l'annuncio",
    before: "/landing/bagno-before.jpg",
    after: "/landing/bagno-after.jpg",
    tagline: "Bianchi puliti, riflessi giusti. Pronta per l'annuncio.",
    tech: "Luce uniforme · bianchi neutri · riflessi e specchi puliti",
  },
  {
    id: "bagnetto",
    label: "Bagnetto",
    badge: "Foto migliorata",
    before: "/landing/bagno-utente-before.jpg",
    after: "/landing/bagno-utente-after.jpg",
    tagline: "Anche il bagno piccolo si presenta bene.",
    tech: "Sistema asciugamani e specchio · superfici nette · niente oggetti random",
  },
  {
    id: "terrazzo",
    label: "Terrazzo",
    badge: "Foto migliorata",
    before: "/landing/terrazzo-before.jpg",
    after: "/landing/terrazzo-after.jpg",
    tagline: "Lo spazio esterno che fa la differenza.",
    tech: "Sistema piante, sedie e tavolino · luce calda al tramonto",
  },
  {
    id: "esterno",
    label: "Esterno",
    badge: "Foto migliorata",
    before: "/landing/esterno-before.jpg",
    after: "/landing/esterno-after.jpg",
    tagline: "Linee dritte, cielo terso. La facciata mostrata bene.",
    tech: "Cielo terso · facciata pulita · linee architettoniche dritte",
  },
  {
    id: "svuota",
    label: "Svuota stanza",
    badge: "Rimuove l'arredo",
    before: "/landing/svuota-before.jpg?v=iter29b",
    after: "/landing/svuota-after.jpg?v=iter29b",
    tagline: "Arredo datato? Lo togli e mostri lo spazio reale.",
    tech: "Toglie mobili e oggetti · pavimento e muri ricostruiti dove erano coperti",
  },
];

export default function FotoMega() {
  // iter v33 r103 · User M1500: il carousel deve partire dalla PRIMA
  // trasformazione ("Camera" = 01/11), non da "Arreda" (03/11) come faceva
  // prima. Cambio il default active da "vuoto" a "camera".
  const [active, setActive] = useState("camera");
  // Indice del thumbnail più visibile sulla strip mobile (per il badge "X/N")
  const [stripIdx, setStripIdx] = useState(0);
  const stripRef = useRef(null);

  const current = ROOMS.find((r) => r.id === active) || ROOMS[0];

  // Quando cambia active, scroll dello strip per centrare il thumbnail
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const btn = strip.querySelector(`[data-roomid="${active}"]`);
    if (!btn) return;
    const stripRect = strip.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const target =
      strip.scrollLeft +
      (btnRect.left - stripRect.left) -
      (stripRect.width / 2 - btnRect.width / 2);
    strip.scrollTo({ left: target, behavior: "smooth" });
  }, [active]);

  const idx = ROOMS.findIndex((r) => r.id === active);
  const goPrev = () => setActive(ROOMS[(idx - 1 + ROOMS.length) % ROOMS.length].id);
  const goNext = () => setActive(ROOMS[(idx + 1) % ROOMS.length].id);

  // Calcola quale thumbnail è "più visibile" nella strip mobile (per badge X/N).
  // Niente IntersectionObserver: basta il centro della strip vs centro dei btn.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    let raf = 0;
    const recompute = () => {
      raf = 0;
      const sr = strip.getBoundingClientRect();
      const center = sr.left + sr.width / 2;
      const btns = strip.querySelectorAll("[data-roomid]");
      let best = 0;
      let bestDist = Infinity;
      btns.forEach((b, i) => {
        const r = b.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const d = Math.abs(c - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setStripIdx(best);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(recompute);
    };
    recompute();
    strip.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      strip.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="feature-foto"
      className="py-16 sm:py-24 lg:py-32 bg-[#1C1C1A] text-white relative overflow-hidden"
      data-testid="feature-foto"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        aria-hidden
      >
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-[160px]"
          style={{ background: "radial-gradient(circle, #7C8B6E50 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
        {/* HEADER */}
        <div className="max-w-3xl mb-10 sm:mb-14">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#A8B89A]/15 border border-[#A8B89A]/25">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A8B89A] animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A8B89A]">
                Live
              </span>
            </span>
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/55">
              Foto immobiliari · {ROOMS.length} trasformazioni
            </span>
          </div>
          <h2
            className="font-editorial text-[36px] sm:text-5xl lg:text-[64px] mt-2 sm:mt-3 leading-[1.0] tracking-tighter"
            data-testid="feature-foto-title"
          >
            Le foto del telefono.
            <br />
            <span className="italic text-[#A8B89A]">Pronte da pubblicare.</span>
          </h2>
          <p className="mt-5 sm:mt-7 text-[16px] sm:text-[18px] text-white/75 leading-relaxed max-w-2xl">
            Carichi la foto che hai scattato col telefono. Pronto sistema luce,
            colori e prospettiva. A te resta solo da pubblicarla — su
            Immobiliare.it, Idealista o dove vuoi.
          </p>
        </div>

        {/* BIG SLIDER */}
        <div className="relative" data-testid="foto-mega-slider">
          <div className="flex items-center gap-2.5 mb-4 sm:mb-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/12 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A8B89A] animate-pulse" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#A8B89A]">
                {current.badge}
              </span>
            </div>
            <span className="text-[11px] text-white/50 tabular-nums">
              {String(idx + 1).padStart(2, "0")} / {String(ROOMS.length).padStart(2, "0")}
            </span>
          </div>

          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-[#1C1C1A]">
            <BeforeAfterSlider
              key={current.id}
              beforeSrc={current.before}
              afterSrc={current.after}
              height="h-[320px] sm:h-[480px] lg:h-[600px]"
              autoPlay
            />

            {/* Prev/Next arrows — iter v31 r41 visibili anche su mobile */}
            <button
              type="button"
              onClick={goPrev}
              className="flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/12 hover:bg-white/22 backdrop-blur-md items-center justify-center transition-colors z-10"
              aria-label="Stanza precedente"
              data-testid="foto-prev"
            >
              <ArrowLeft size={18} weight="bold" className="text-white" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/12 hover:bg-white/22 backdrop-blur-md items-center justify-center transition-colors z-10"
              aria-label="Stanza successiva"
              data-testid="foto-next"
            >
              <ChevR size={18} weight="bold" className="text-white" />
            </button>
          </div>

          {/* Caption sotto slider */}
          <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 max-w-3xl">
            <p
              className="font-editorial text-[20px] sm:text-[24px] text-white leading-tight"
              data-testid="foto-mega-caption"
            >
              {current.tagline}
            </p>
            <p className="text-[12px] text-white/55 sm:text-right shrink-0 sm:max-w-[260px]">
              {current.tech}
            </p>
          </div>
        </div>

        {/* THUMBNAIL STRIP — tutte le stanze visibili.
            r54 — fix bug desktop: con mouse normale la strip horizontal-scroll
            non era usabile (serve shift+wheel). Da `lg` in su uso una griglia
            che mostra TUTTE le 9 stanze contemporaneamente. Sotto resta
            scroll orizzontale per touch. */}
        <div
          ref={stripRef}
          className="mt-8 sm:mt-12 pt-2 -mx-5 sm:-mx-8 lg:-mx-10 px-5 sm:px-8 lg:px-10 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 hide-scroll lg:grid lg:grid-cols-11 lg:overflow-visible lg:snap-none"
          data-testid="foto-mega-thumbs"
          role="tablist"
          aria-label="Tipi di trasformazione"
        >
          {ROOMS.map((r) => {
            const isActive = r.id === active;
            return (
              <button
                key={r.id}
                type="button"
                data-roomid={r.id}
                data-testid={`foto-thumb-${r.id}`}
                role="tab"
                aria-selected={isActive}
                aria-label={r.label}
                onClick={() => setActive(r.id)}
                className={`relative shrink-0 w-[108px] sm:w-[148px] lg:w-full text-left snap-start group transition-transform duration-300 focus:outline-none focus-visible:outline-none [-webkit-tap-highlight-color:transparent] rounded-xl cursor-pointer ${
                  isActive ? "scale-[1.04]" : "hover:-translate-y-0.5"
                }`}
              >
                <div
                  className={`relative aspect-[4/3] rounded-xl overflow-hidden transition-all duration-300 ${
                    isActive
                      ? "ring-[3px] ring-[#A8B89A] shadow-[0_8px_24px_-8px_rgba(168,184,154,0.5)]"
                      : "ring-2 ring-white/35 group-hover:ring-white/70"
                  }`}
                >
                  <img
                    src={r.after}
                    alt={r.label}
                    className={`w-full h-full object-cover transition-all duration-500 ${
                      isActive ? "" : "opacity-75 group-hover:opacity-100"
                    }`}
                    loading="lazy"
                    draggable={false}
                  />
                  {isActive && (
                    <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#A8B89A] shadow-[0_0_10px_rgba(168,184,154,0.8)] animate-pulse" />
                  )}
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <span
                    className={`text-[11.5px] font-semibold leading-tight transition-colors ${
                      isActive ? "text-[#A8B89A]" : "text-white/65 group-hover:text-white/90"
                    }`}
                  >
                    {r.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Indicatore "X / N" — solo mobile (strip orizzontale).
            Aggiornato live dallo scroll della strip. Su lg: tutto è già
            visibile in griglia, quindi lo nascondiamo. */}
        <div
          className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-white/55 lg:hidden tabular-nums"
          data-testid="foto-mega-strip-counter"
          aria-live="polite"
        >
          <span className="text-white/85 font-semibold">{stripIdx + 1}</span>
          <span className="text-white/35">/</span>
          <span>{ROOMS.length}</span>
          <span className="ml-1 text-white/35">trasformazioni</span>
        </div>

        {/* CTA — iter v31 r61 — User: «"Prova con una tua foto" sotto è
            doppione con la sezione LiveTryThree subito sotto che dice già
            "Senza registrarti, provalo adesso". Tolto per chiarezza, lo
            scroll porta naturalmente alla sezione di prova interattiva.» */}
      </div>
    </section>
  );
}
